import type { Position, RouteData } from '../types.js';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector.js';
import type { RouteFullData } from '../types/storage.js';
import { RouteStorageManager, StorageType } from '../services/storage';
import type { SessionFunctions } from '../services/storage/RouteStorageManager.js';
import { calculateRoute, calculateDistance } from './routeCalculation.js';
import { downloadGPXFromFullData } from './gpxGenerator.js';
import { AnalyticsService } from '../services/analytics/HybridAnalyticsService';
import { aggregateElevationData } from './elevationAggregation';
import { detectClimbs, DEFAULT_CLIMB_CONFIG } from './climbDetection';

// Helper function to calculate steepness (copied from routeCalculation.ts)
const calculateSteepness = (elevation1: number, elevation2: number, distance: number): number => {
  if (distance === 0) return 0;
  return ((elevation2 - elevation1) / distance) * 100;
};

/**
 * Convert RouteFullData back to RouteData format for display
 */
export const convertFullDataToRouteData = (fullData: RouteFullData): RouteData | null => {
  try {
    if (!fullData.route_geometry || fullData.route_geometry.type !== 'LineString') {
      console.error('Invalid route geometry in full data');
      return null;
    }

    // First, pre-calculate accumulated distances for all coordinates
    const distanceData: number[] = [];
    let accumulatedDistance = 0;
    
    fullData.route_geometry.coordinates.forEach((coord: number[], index: number, array: number[][]) => {
      if (index > 0) {
        const segmentDistance = calculateDistance(
          array[index-1][1], 
          array[index-1][0], 
          coord[1], 
          coord[0]
        );
        accumulatedDistance += segmentDistance / 1000; // Convert to km
      }
      distanceData.push(accumulatedDistance);
    });

    // Create coordinates array WITH accumulated distance
    const coordinates = fullData.route_geometry.coordinates.map((coord: number[], index: number) => ({
      lng: coord[0],
      lat: coord[1],
      distance: distanceData[index] // Add accumulated distance to each coordinate
    }));

    // Extract properties from route properties or use defaults
    const properties = fullData.route_properties || {};
    
    // Calculate elevation data from coordinates
    const elevationData = fullData.route_geometry.coordinates.map((coord: number[], index: number, array: number[][]) => {
      const elevation = coord[2] || 0;
      const segmentDistance = index > 0 
        ? calculateDistance(array[index-1][1], array[index-1][0], coord[1], coord[0]) 
        : 0;

      return {
        distance: distanceData[index], // Use pre-calculated accumulated distance
        elevation,
        steepness: calculateSteepness(
          index > 0 ? array[index-1][2] || 0 : elevation,
          elevation,
          segmentDistance
        )
      };
    });

    // Detect climbs on FULL elevation data before aggregation for accuracy
    const climbs = detectClimbs(elevationData, DEFAULT_CLIMB_CONFIG);
    
    // Aggregate each climb's elevation profile to reduce data
    const aggregatedClimbs = climbs.map(climb => ({
      ...climb,
      elevationProfile: aggregateElevationData(climb.elevationProfile, 150)
    }));

    // Aggregate elevation data to 150 points for optimal chart performance
    // This prevents lag by reducing the amount of data the chart needs to process
    const aggregatedElevationData = aggregateElevationData(elevationData, 150);

    return {
      coordinates,
      distance: properties['track-length'] || distanceData[distanceData.length - 1] * 1000, // Convert back to meters if needed
      duration: properties['total-time'] || 0,
      ascend: properties['filtered ascend'] || 0,
      elevationData: aggregatedElevationData, // Use aggregated data for performance
      fullElevationData: elevationData, // Store full resolution data for zoom re-aggregation
      segments: properties.segments || [], // Restored processed segments
      rawSegments: properties.rawSegments || [], // Restored raw segments for duration calculation
      climbs: aggregatedClimbs // Pre-calculated climbs from full resolution data
    };
  } catch (error) {
    console.error('Error converting full route data to route data:', error);
    return null;
  }
};

/**
 * Save full route data after successful route calculation
 */
export const saveFullRouteData = async (
  routeId: string, 
  routeData: RouteData, 
  profile: RouteProfile,
  rawGeoJSON?: any,
  sessionFunctions?: SessionFunctions
): Promise<boolean> => {
  try {

    const storageManager = new RouteStorageManager({
      type: StorageType.SUPABASE,
      endpoint: import.meta.env.VITE_SUPABASE_URL,
      apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    }, sessionFunctions);

    // First check if the route exists and belongs to the user
    const routeCheck = await storageManager.getRoute(routeId);
    if (!routeCheck.success || !routeCheck.data) {
      console.warn('⚠️ Route does not exist or user does not have access:', routeId);
      console.warn('⚠️ Skipping full route data save for non-existent/inaccessible route');
      return false;
    }

    // Convert RouteData back to GeoJSON format for storage
    // Use the original BRouter geometry if available, otherwise reconstruct
    const routeGeometry: GeoJSON.LineString = rawGeoJSON?.features?.[0]?.geometry || {
      type: 'LineString',
      coordinates: routeData.coordinates.map((coord) => {
        // Use fullElevationData if available (has all points), otherwise fall back to aggregated
        const elevationSource = routeData.fullElevationData || routeData.elevationData || [];
        
        // Find the closest elevation point by distance
        let elevation = 0;
        if (coord.distance !== undefined && elevationSource.length > 0) {
          const coordDist = coord.distance;
          const closestPoint = elevationSource.reduce((prev, curr) => {
            return Math.abs(curr.distance - coordDist) < Math.abs(prev.distance - coordDist) ? curr : prev;
          }, elevationSource[0]);
          elevation = closestPoint.elevation;
        }
        
        return [coord.lng, coord.lat, elevation];
      })
    };

    // Prepare route properties with all available data from BRouter
    const routeProperties = {
      // Core route statistics
      'track-length': routeData.distance,
      'total-time': routeData.duration,
      'filtered ascend': routeData.ascend,
      
      // Store the complete original BRouter properties (contains messages, etc.)
      originalBRouterProperties: rawGeoJSON?.features?.[0]?.properties || {},
      
      // Include messages which contain surface and road type information
      messages: rawGeoJSON?.features?.[0]?.properties?.messages || [],
      
      // Store processed segments data for frontend use
      segments: routeData.segments || [],
      rawSegments: routeData.rawSegments || [],
      
      // Add any other properties from the original BRouter response
      ...(rawGeoJSON?.features?.[0]?.properties || {})
    };

    const fullData: Omit<RouteFullData, 'id' | 'route_id' | 'created_at' | 'updated_at'> = {
      route_geometry: routeGeometry,
      route_properties: routeProperties,
      routing_engine: 'brouter',
      routing_profile: profile,
      routing_version: '1.7.7', // Current BRouter version
      routing_timestamp: new Date(),
      coordinates_count: routeGeometry.coordinates.length,
      is_compressed: false,
      compression_algorithm: undefined
    };

    const result = await storageManager.saveFullRouteData(routeId, fullData);
    
    if (result.success) {
      return true;
    } else {
      console.error('❌ Failed to save full route data:', result.error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error saving full route data:', error);
    return false;
  }
};

/**
 * Load route with full data if available, fallback to recalculation
 */
export const loadRouteWithFullData = async (
  routeId: string,
  positions: Position[],
  profile: RouteProfile,
  sessionFunctions?: SessionFunctions
): Promise<RouteData | null> => {
  try {
    const storageManager = new RouteStorageManager({
      type: StorageType.SUPABASE,
      endpoint: import.meta.env.VITE_SUPABASE_URL,
      apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    }, sessionFunctions);

    // Try to get full route data first
    const fullDataResult = await storageManager.getFullRouteData(routeId);
    
    if (fullDataResult.success && fullDataResult.data) {
      
      // Check if the profile matches - if not, we need to recalculate
      if (fullDataResult.data.routing_profile === profile) {
        const routeData = convertFullDataToRouteData(fullDataResult.data);
        if (routeData) {
          return routeData;
        }
      }
    }

    // Fallback to recalculation
    const routeData = await calculateRoute(positions, profile, 'geojson', routeId, sessionFunctions, true); // bypassCache = true
    
    return routeData;
  } catch (error) {
    console.error('Error in loadRouteWithFullData:', error);
    // Fallback to regular calculation
    return calculateRoute(positions, profile, 'geojson', routeId, sessionFunctions, true); // bypassCache = true
  }
};

/**
 * Enhanced GPX download that uses stored full route data when available
 * This is the main GPX download function that should be used throughout the app
 */
export const downloadRouteAsGPXWithFullData = async (
  routeId: string | null,
  positions: Position[],
  profile: RouteProfile,
  routeName?: string,
  onLoadingStart?: () => void,
  onLoadingEnd?: () => void,
  sessionFunctions?: SessionFunctions
): Promise<void> => {
  try {
    
    // Notify that loading has started
    if (onLoadingStart) {
      onLoadingStart();
    }

    // If we have a route ID, try to use stored full data first
    if (routeId) {
      try {
        const storageManager = new RouteStorageManager({
          type: StorageType.SUPABASE,
          endpoint: import.meta.env.VITE_SUPABASE_URL,
          apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        }, sessionFunctions);

        const fullDataResult = await storageManager.getFullRouteData(routeId);
        
        if (fullDataResult.success && fullDataResult.data) {
          // Check if the profile matches
          if (fullDataResult.data.routing_profile === profile) {
            downloadGPXFromFullData(fullDataResult.data, routeName);
            
            // Track GPX download analytics
            try {
              const pointsCount = fullDataResult.data.route_geometry?.coordinates?.length || positions.length;
              const estimatedFileSize = pointsCount * 150; // Rough estimate: ~150 bytes per point
              const fileName = routeName 
                ? `${routeName.replace(/[^a-zA-Z0-9-_]/g, '_')}.gpx`
                : `cycling-route-${new Date().toISOString().split('T')[0]}.gpx`;
              
              AnalyticsService.trackGPXDownloaded({
                filename: fileName, // Used only locally, not stored
                source: 'route_export',
                fileSizeBytes: estimatedFileSize,
                pointsCount: pointsCount,
                routeStorageId: routeId || undefined,
                bikeProfile: profile
              });
            } catch (error) {
              console.warn('Failed to track GPX download analytics:', error);
            }
            
            return;
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load stored full data, falling back to API:', error);
      }
    }

    // Fallback to API call
    const gpxData = await calculateRoute(positions, profile, 'gpx', undefined, sessionFunctions);
    
    if (!gpxData || typeof gpxData !== 'string') {
      throw new Error('No GPX data received from API or data is not a string');
    }

    // Download the GPX file from API response
    const blob = new Blob([gpxData as string], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const fileName = routeName 
      ? `${routeName.replace(/[^a-zA-Z0-9-_]/g, '_')}.gpx`
      : `cycling-route-${new Date().toISOString().split('T')[0]}.gpx`;
    
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // Track GPX download analytics (API fallback)
    try {
      AnalyticsService.trackGPXDownloaded({
        filename: fileName, // Used only locally, not stored
        source: 'route_export',
        fileSizeBytes: blob.size,
        pointsCount: positions.length,
        routeStorageId: routeId || undefined,
        bikeProfile: profile
      });
    } catch (error) {
      console.warn('Failed to track GPX download analytics:', error);
    }
    
  } catch (error) {
    console.error('❌ Error in enhanced GPX download:', error);
    throw error;
  } finally {
    // Notify that loading has ended
    if (onLoadingEnd) {
      onLoadingEnd();
    }
  }
};

// Alias for backwards compatibility and convenience
export const downloadRouteAsGPX = downloadRouteAsGPXWithFullData;
